import { StylesGroup } from '../types';
import {
  BORDER_WIDTH_CSS_PROPERTY_TYPES,
  getAllowedPropertiesForAction,
} from '../../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import { Meaningfulness } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/meaningfulnessPredicates';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import {
  hasAnyMeaningfulValueCheckContext,
  isAllowedStylePresent,
} from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/actionValidators/actionVisibilityRules';
import { buildRootContextInfosMap } from '../common/grouping/helpers/buildRootContextInfosMap';
import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';

const checkGroupValues = (group: StylesGroup) => {
  return hasAnyMeaningfulValueCheckContext(
    group.context,
    new Set(BORDER_WIDTH_CSS_PROPERTY_TYPES),
    Meaningfulness.meaningCheck(StylesDerivedNativeActions.borderStyles),
    group.styleNamespace,
    new Set(group.items.map((item) => item.styleItemId)),
  );
};

/**
 * Border group display filter
 *
 * Filters component groups in the border panel based on border width values.
 *
 * Logic for each root component and its children:
 * 1. If ANY group (root or child) has border width > 0:
 *    - Show only groups that have border width > 0 (filters both roots and children)
 * 2. If NO group has border width > 0:
 *    - Show only root groups that are valid for border properties (have manifest support)
 *    - Hide child groups
 */
export const borderGroupDisplayFilter = (
  groups: StylesGroup[],
  rootContexts: VirtualManifestAPI[],
): StylesGroup[] => {
  if (groups.length === 0) {
    return groups;
  }

  const result: StylesGroup[] = [];
  const rootContextInfos = buildRootContextInfosMap(groups, rootContexts);

  for (const rootContextInfo of Object.values(rootContextInfos)) {
    const contextGroups = [
      ...rootContextInfo.rootGroups,
      ...rootContextInfo.childGroups,
    ];

    const isSomeBorderWidthVisible = contextGroups.some(checkGroupValues);

    if (isSomeBorderWidthVisible) {
      result.push(...contextGroups.filter(checkGroupValues));
      continue;
    }

    for (const rootGroup of rootContextInfo.rootGroups) {
      if (
        isAllowedStylePresent(
          rootGroup.context,
          new Set(
            getAllowedPropertiesForAction(
              StylesDerivedNativeActions.borderStyles,
            ),
          ),
          rootGroup.styleNamespace,
        )
      ) {
        result.push(rootGroup);
      }
    }
  }

  return result;
};
